// W37b (2026-10-20): workspace role carried on the HubSpot session.
//
// The api-server attaches `role: 'manager' | 'sales_rep'` to every
// verified session from a small allow-list of HubSpot USER ids
// (services/api-server/middleware/hubspot-session.mjs, env
// WORKSPACE_MANAGER_USER_IDS, default 89608213 = Doug; READ 2026-10-20).
// The workspace has no whoami route, so the session parsers mirror the
// SAME rule client-side: the url-bootstrap session takes the user id off
// the deep link, the signed session takes it off the payload, and
// RequireRole (via useRep) reads `session.role`.
//
// The UI gate is cosmetic. The server still answers 403
// MANAGER_ROLE_REQUIRED to a sales_rep session on /v1/team-live/overview
// and /v1/executive/summary, and the Team Live / Executive Live screens
// render that 403 as "restricted". If the two lists ever drift the
// server wins; the build-time env VITE_WORKSPACE_MANAGER_USER_IDS
// (comma / space separated) keeps them in step.

export type WorkspaceRole = 'sales_rep' | 'manager' | 'executive';

export const DEFAULT_MANAGER_USER_IDS: readonly string[] = Object.freeze(['89608213']);
export const ROLE_MANAGER: WorkspaceRole = 'manager';
export const ROLE_SALES_REP: WorkspaceRole = 'sales_rep';

const ID_RX = /^[A-Za-z0-9._:-]{1,64}$/;

/** Same parse as the api-server: comma / whitespace separated ids, default list when empty. */
export function parseManagerUserIds(raw: string | null | undefined): ReadonlySet<string> {
  const text = typeof raw === 'string' ? raw.trim() : '';
  if (!text) return new Set(DEFAULT_MANAGER_USER_IDS);
  const ids = text
    .split(/[,\s]+/)
    .map((v) => v.trim())
    .filter((v) => ID_RX.test(v));
  return ids.length > 0 ? new Set(ids) : new Set(DEFAULT_MANAGER_USER_IDS);
}

function readEnvManagerIds(): string | undefined {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  return env?.VITE_WORKSPACE_MANAGER_USER_IDS;
}

export function isWorkspaceRole(value: unknown): value is WorkspaceRole {
  return value === 'sales_rep' || value === 'manager' || value === 'executive';
}

/**
 * Role for a HubSpot user id. `managerIds` defaults to the build-time env
 * list (or the server default 89608213). Unknown / empty ids are sales_rep.
 */
export function roleForUserId(
  userId: string | null | undefined,
  managerIds: ReadonlySet<string> = parseManagerUserIds(readEnvManagerIds()),
): WorkspaceRole {
  if (typeof userId !== 'string' || userId.length === 0) return ROLE_SALES_REP;
  return managerIds.has(userId) ? ROLE_MANAGER : ROLE_SALES_REP;
}
